import { compressToEncodedURIComponent, decompressFromEncodedURIComponent } from "lz-string";
import type { InputParam } from "../types/grid.ts";
import { ParamType } from "../types/grid.ts";

/**
 * Compact filter representation. Short keys minimise URL length.
 * c = sColumn, v = oValue, t = Type (omitted when 0 = Equal).
 *
 * Unused (`t:11`) is the UI-state sentinel emitted by ShowCheckboxFilter when
 * the checkbox is in the "no server filter" state — server pipeline drops the
 * param entirely (DataGrid.vb:165), but the client uses the carried `v` to
 * recover the checkbox state after a URL round-trip. The carried `v` is the
 * raw checked boolean; `v:true` is the overwhelmingly common case (a checked
 * FilterShowCheckBox), so we omit it on encode and default it back to `true`
 * on decode. `v:false` (e.g. unchecked FilterHideCheckbox) still serialises.
 */
interface CompactParam {
  c: string;
  v?: unknown;
  t?: number;
}

const COMPRESS_THRESHOLD = 500;
const COMPRESSED_PREFIX = "z:";

function toCompact(filters: InputParam[]): CompactParam[] {
  return filters.map(f => {
    const p: CompactParam = { c: f.sColumn };
    const isUnused = f.Type === ParamType.Unused;
    // Strip only the boolean-true / null / undefined shorthand. Empty strings
    // (e.g. cleared date filter) MUST round-trip explicitly — otherwise
    // fromCompact's "missing v + Unused → true" fallback misreads a date
    // input as the literal string "true". See dateFilter.tsx initial state.
    if (f.oValue != null && !(isUnused && f.oValue === true)) {
      p.v = f.oValue;
    }
    if (f.Type != null && f.Type !== ParamType.Equal) p.t = f.Type;
    return p;
  });
}

function fromCompact(items: CompactParam[]): InputParam[] {
  return items.map(p => {
    const type = (p.t ?? ParamType.Equal) as ParamType;
    const oValue = p.v === undefined && type === ParamType.Unused ? true : p.v;
    return { sColumn: p.c, oValue, Type: type };
  });
}

/** Raw compact JSON string — for sessionStorage (no URL encoding). */
export function filtersToCompactJson(filters: InputParam[]): string {
  if (!filters.length) return "";
  return JSON.stringify(toCompact(filters));
}

/** Parse raw compact JSON back to filters. */
export function filtersFromCompactJson(json: string): InputParam[] | null {
  if (!json) return null;
  try {
    const parsed = JSON.parse(json);
    if (!Array.isArray(parsed)) return null;
    return fromCompact(parsed);
  } catch {
    return null;
  }
}

/**
 * Encoded form for use as a URLSearchParams value — returns raw JSON (or compressed prefix).
 * URLSearchParams handles URL encoding automatically; don't pre-encode.
 */
export function encodeFilters(filters: InputParam[]): string {
  const json = filtersToCompactJson(filters);
  if (!json) return "";
  if (json.length > COMPRESS_THRESHOLD) {
    // compressToEncodedURIComponent is URL-safe (no special chars)
    return COMPRESSED_PREFIX + compressToEncodedURIComponent(json);
  }
  return json;
}

/** Decode the form produced by encodeFilters (post URLSearchParams.get). */
export function decodeFilters(encoded: string): InputParam[] | null {
  if (!encoded) return null;
  try {
    let json: string;
    if (encoded.startsWith(COMPRESSED_PREFIX)) {
      json = decompressFromEncodedURIComponent(encoded.slice(COMPRESSED_PREFIX.length)) ?? "";
    } else {
      json = encoded; // URLSearchParams already decoded it
    }
    return filtersFromCompactJson(json);
  } catch {
    return null;
  }
}

